import * as joint from '@joint/plus';

export class Div extends joint.shapes.standard.Rectangle {
  defaults() {
    return {
      ...super.defaults,
      maxDestinations: Infinity,
      type: 'appShapes.Div',
      name: 'DIV',
      attrs: {
        body: {
          fill: '#FFFFFF',
          stroke: '#000000',
          strokeWidth: 1,
          refWidth: '100%',
          refHeight: '100%',
        },
        label: {
          text: 'DIV',
          fill: '#000000',
          fontFamily: 'Open Sans',
          fontWeight: 'Normal',
          fontSize: 6,
          textVerticalAnchor: 'middle',
          textAnchor: 'middle',
          refX: '50%',
          refY: '50%',
        },
      },
    };
  }
}

export class Tri extends joint.shapes.standard.Ellipse {
  constructor(...args) {
    super(...args);
    this.markup = [
      { tagName: 'ellipse', selector: 'body' },
      { tagName: 'text', selector: 'label' },
      { tagName: 'path', selector: 'chevron1' },
      { tagName: 'path', selector: 'chevron2' },
    ];
    this.portMarkup = [
      {
        tagName: 'circle',
        selector: 'portBody',
        attributes: {
          magnet: true,
          r: 4,
          fill: 'white',
          stroke: 'black',
          'stroke-width': 1,
        },
      },
    ];
    this.portLabelMarkup = [
      {
        tagName: 'text',
        selector: 'portLabel',
        attributes: { fontSize: 6, fill: '#ffffff' },
      },
    ];
  }

  defaults() {
    const base = super.defaults;
    const triDefaults = {
      maxDestinations: 1,
      type: 'appShapes.Tri',
      name: 'TRI',
      sens: false,
      size: { width: 100, height: 40 },
      attrs: {
        body: {
          fill: '#FFFFFF',
          stroke: '#000000',
          strokeWidth: 1,
          refCx: '50%',
          refCy: '50%',
          refRx: '50%',
          refRy: '50%',
        },
        label: {
          text: 'TRI',
          fill: '#000000',
          fontFamily: 'Open Sans',
          fontWeight: 'Normal',
          fontSize: 14,
          textVerticalAnchor: 'middle',
          textAnchor: 'middle',
          refX: '50%',
          refY: '50%',
        },
        chevron1: {
          fill: 'none',
          stroke: '#ffffff',
          strokeWidth: 1,
        },
        chevron2: {
          fill: 'none',
          stroke: '#ffffff',
          strokeWidth: 1,
        },
      },
      ports: {
        groups: {
          auto: {
            position: { name: 'ellipseSpread' },
            label: { position: { name: 'outside' } },
          },
        },
        items: [
          { id: 'port1', group: 'auto', time: 0 },
          { id: 'port2', group: 'auto', time: 0 },
          { id: 'port3', group: 'auto', time: 0 },
          { id: 'port4', group: 'auto', time: 0 },
        ],
      },
    };
    return { ...base, ...triDefaults };
  }

  initialize(...args) {
    super.initialize(...args);
    this.on('change:size', this.updateChevrons, this);
    this.on('change:sens', this.updateChevrons, this);
    this.on('change:ports', this.updatePortLabels, this);
    this.updateChevrons();
  }

  updatePortLabels() {
    const ports = this.getPorts();
    ports.forEach((port, i) => {
      this.portProp(port.id, 'attrs/portLabel/text', String(i + 1));
    });
  }

  updateChevrons() {
    const { width, height } = this.size();
    const reversed = this.get('sens');
    const size = 4;
    const rx = width / 2;
    const ry = height / 2;
    const xLeft = rx * (1 - Math.cos(0.5));
    const xRight = width - xLeft;
    const yTop = ry * (1 - Math.sin(0.5));
    const yBottom = height - yTop;
    const radianAngle = Math.atan(ry / rx);
    const degreeAngle = (360 * radianAngle) / (2 * Math.PI);

    let topRight = [
      `M ${xRight - size} ${yTop - size}`,
      `L ${xRight} ${yTop}`,
      `L ${xRight - size} ${yTop + size}`,
    ].join(' ');
    let bottomLeft = [
      `M ${xLeft + size} ${yBottom - size}`,
      `L ${xLeft} ${yBottom}`,
      `L ${xLeft + size} ${yBottom + size}`,
    ].join(' ');

    if (reversed) {
      topRight = [`M ${xRight + size} ${yTop - size}`, `L ${xRight} ${yTop}`, `L ${xRight + size} ${yTop + size}`].join(
        ' '
      );
      bottomLeft = [
        `M ${xLeft - size} ${yBottom - size}`,
        `L ${xLeft} ${yBottom}`,
        `L ${xLeft - size} ${yBottom + size}`,
      ].join(' ');
    }

    this.attr('chevron1/transform', `rotate(${degreeAngle}, ${xRight}, ${yTop})`);
    this.attr('chevron2/transform', `rotate(${degreeAngle}, ${xLeft}, ${yBottom})`);
    this.attr('chevron1/d', topRight);
    this.attr('chevron2/d', bottomLeft);
  }
}
export class Stock extends joint.shapes.standard.Rectangle {
  constructor(...args) {
    super(...args);

    this.markup = [
      { tagName: 'path', selector: 'body' },
      { tagName: 'ellipse', selector: 'top' },
      { tagName: 'ellipse', selector: 'bottom' },
      { tagName: 'text', selector: 'label' },
    ];
  }

  defaults() {
    return {
      ...super.defaults,
      type: 'appShapes.Stock',
      size: { width: 60, height: 100 },
      name: 'STOCK',
      attrs: {
        body: {
          d: '',
          fill: '#ffffff',
          stroke: '#FFFFFF',
          strokeWidth: 1,
        },
        top: {
          cx: '50%',
          rx: '50%',
          ry: '10%',
          cy: 10,
          fill: '#ffffff',
          stroke: '#000000',
          strokeWidth: 1,
        },
        bottom: {
          cx: '50%',
          rx: '50%',
          ry: '10%',
          cy: 10,
          fill: '#ffffff',
          stroke: '#ffffff',
          strokeWidth: 1,
        },
        label: {
          text: 'STOCK',
          fill: '#ffffff',
          fontFamily: 'Open Sans',
          fontWeight: 'bold',
          fontSize: 10,
          refX: '50%',
          refY: '50%',
          textAnchor: 'middle',
          textVerticalAnchor: 'middle',
        },
      },
    };
  }

  initialize(...args) {
    super.initialize(...args);
    this.on('change:size', this.updatePath, this);
    this.updatePath();
  }

  updatePath() {
    const { width, height } = this.size();
    const rx = width / 2;
    const ry = height * 0.1; // 10% for elliptical height
    const topY = ry;
    const bottomY = height - ry;

    const path = [
      `M ${0} ${topY}`,
      `A ${rx} ${ry} 0 0 1 ${width} ${topY}`, // Top ellipse front
      `L ${width} ${bottomY}`,
      `A ${rx} ${ry} 0 0 1 ${0} ${bottomY}`, // Bottom ellipse front
      `Z`,
    ].join(' ');

    this.attr('body/d', path);
    this.attr('top/cx', width / 2);
    this.attr('top/cy', topY);
    this.attr('top/rx', rx);
    this.attr('top/ry', ry);

    this.attr('bottom/cx', width / 2);
    this.attr('bottom/cy', bottomY);
    this.attr('bottom/rx', rx);
    this.attr('bottom/ry', ry);
  }
}
